"use client";

import { forwardRef, useCallback, useRef, type ReactNode } from "react";
import { Search, X } from "lucide-react";
import { Input, type InputSize } from "./Input";

// Phase 4.3 — the accessible search field, replacing `ui/SearchInput`.
//
// What it fixes: `ui/SearchInput` had a placeholder and nothing else, so its
// accessible name was empty. A screen-reader user tabbing into a filter bar
// heard "edit text, blank" — on a page whose entire purpose is filtering. It
// also had no way to clear itself, so narrowing a 4,000-row alert list and
// then backing out meant selecting the text and deleting it by hand.
//
// Three things every search box in this product should have, and now does in
// one place: a real (visually hidden) label, a leading magnifier that the
// text can never collide with, and a clear button that appears only when
// there is something to clear and returns focus to the field afterwards —
// so clearing does not dump the user back at the top of the tab order.
//
// `type="search"` is deliberate: it gives the Escape-to-clear behaviour for
// free in WebKit, and assistive tech announces the role as searchbox.
export interface SearchFieldProps {
  value: string;
  onChange: (next: string) => void;
  /** The accessible name. Visually hidden unless `showLabel`. */
  label?: string;
  placeholder?: string;
  size?: InputSize;
  showLabel?: boolean;
  autoFocus?: boolean;
  className?: string;
  id?: string;
  /** Fires on Enter — for searches that run on submit rather than per keystroke. */
  onSubmit?: (value: string) => void;
  help?: ReactNode;
  disabled?: boolean;
}

export const SearchField = forwardRef<HTMLInputElement, SearchFieldProps>(function SearchField(
  { value, onChange, label = "Search", placeholder, size = "md", showLabel, autoFocus, className, id, onSubmit, help, disabled },
  ref
) {
  const innerRef = useRef<HTMLInputElement | null>(null);

  const setRef = useCallback(
    (node: HTMLInputElement | null) => {
      innerRef.current = node;
      if (typeof ref === "function") ref(node);
      else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
    },
    [ref]
  );

  const clear = useCallback(() => {
    onChange("");
    // Focus goes back to the field, not to wherever the DOM order would send
    // it — the clear button is about to disappear, and losing focus to <body>
    // on a keyboard-driven clear is disorienting.
    innerRef.current?.focus();
  }, [onChange]);

  return (
    <Input
      ref={setRef}
      id={id}
      type="search"
      label={label}
      hideLabel={!showLabel}
      help={help}
      size={size}
      disabled={disabled}
      autoFocus={autoFocus}
      className={className}
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && onSubmit) onSubmit(value);
        // Escape clears rather than bubbling — but only when there is text.
        // An empty search box must let Escape through to the dialog or
        // drawer around it, or the field becomes a keyboard trap.
        if (e.key === "Escape" && value) {
          // preventDefault marks the key as handled, so the overlay stack
          // (which runs in the bubble phase) leaves the dialog open.
          e.preventDefault();
          e.stopPropagation();
          clear();
        }
      }}
      leadingIcon={<Search size={size === "sm" ? 13 : 15} />}
      trailingSlot={
        value ? (
          <button type="button" className="rc-input__clear" onClick={clear} aria-label={`Clear ${label.toLowerCase()}`}>
            <X size={13} aria-hidden />
          </button>
        ) : null
      }
    />
  );
});
